// In-depth DSA lessons (Codeazy lesson standard). Code is JavaScript.

export const dsaLessons = [
  {
    slug: "big-o",
    title: "Big-O and Complexity Analysis",
    summary: "Measure how code scales, analyse loops and recursion, and use constraints to pick the right approach in interviews.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Two solutions can both be “correct”, yet one finishes in milliseconds and the other takes hours on real data. Big-O is the shared language for comparing them. In every coding interview, you will be asked for the time and space complexity of your solution, and whether you can do better." },

      { type: "h2", text: "The mental model: how does work grow?" },
      { type: "p", text: "Big-O doesn’t measure seconds. It describes **how the amount of work grows as the input grows**. If you double the number of students, does your code do the same work (O(1)), twice the work (O(n)) or four times the work (O(n²))? Constants and small terms are dropped, because at scale only the dominant growth matters." },
      { type: "table", headers: ["Big-O", "Name", "n = 1,000", "n = 1,000,000", "Example"], rows: [
        ["O(1)", "Constant", "1", "1", "Array index, hash map lookup"],
        ["O(log n)", "Logarithmic", "~10", "~20", "Binary search"],
        ["O(n)", "Linear", "1,000", "1,000,000", "One pass over an array"],
        ["O(n log n)", "Linearithmic", "~10,000", "~20,000,000", "Merge sort, efficient sorting"],
        ["O(n²)", "Quadratic", "1,000,000", "10¹² (far too slow)", "Nested loops over all pairs"],
        ["O(2ⁿ)", "Exponential", "astronomical", "", "All subsets"],
        ["O(n!)", "Factorial", "astronomical", "", "All permutations"]
      ] },

      { type: "h2", text: "Rules for calculating Big-O" },
      { type: "ol", items: [
        "**Drop constants:** O(2n) → O(n). O(n/2) → O(n).",
        "**Drop smaller terms:** O(n² + n) → O(n²).",
        "**Sequential steps add:** a loop over n followed by a loop over m is O(n + m).",
        "**Nested steps multiply:** a loop over n inside a loop over m is O(n × m).",
        "**Different inputs get different letters:** don’t call it O(n²) if the two loops run over different arrays."
      ] },
      { type: "code", language: "javascript", code: `function example(students, courses) {
  let total = 0;
  for (const s of students) total += s.score;      // O(n)

  for (const s of students) {                       // O(n × m)
    for (const c of courses) {
      if (s.enrolled.includes(c.id)) total++;       // includes is O(k)! → O(n × m × k)
    }
  }
  return total;                                     // overall: O(n × m × k)
}` },
      { type: "callout", variant: "warning", text: "Hidden loops are the most common analysis mistake. `includes`, `indexOf`, `slice`, `splice`, `shift`, spread `[...arr]` and string concatenation in a loop are all O(n) by themselves." },

      { type: "h2", text: "The same problem at three complexities" },
      { type: "p", text: "Problem: does an array contain two numbers that add up to `target`?" },
      { type: "code", language: "javascript", code: `// O(n²) time, O(1) space: check every pair
function hasPairBrute(nums, target) {
  for (let i = 0; i < nums.length; i++)
    for (let j = i + 1; j < nums.length; j++)
      if (nums[i] + nums[j] === target) return true;
  return false;
}

// O(n log n) time: sort, then two pointers
function hasPairSorted(nums, target) {
  const a = [...nums].sort((x, y) => x - y);
  let l = 0, r = a.length - 1;
  while (l < r) {
    const sum = a[l] + a[r];
    if (sum === target) return true;
    sum < target ? l++ : r--;
  }
  return false;
}

// O(n) time, O(n) space: remember what we've seen
function hasPairHash(nums, target) {
  const seen = new Set();
  for (const x of nums) {
    if (seen.has(target - x)) return true;
    seen.add(x);
  }
  return false;
}

console.log(hasPairBrute([3, 8, 1, 5], 9), hasPairSorted([3, 8, 1, 5], 9), hasPairHash([3, 8, 1, 5], 9));`, output: `true true true` },
      { type: "p", text: "This is the classic **time–space trade-off**: the hash version uses O(n) extra memory to save time." },

      { type: "h2", text: "Logarithms: halving" },
      { type: "p", text: "O(log n) appears whenever the problem size is **halved** (or divided by any constant) at each step. Starting from 1,000,000 and halving, you reach 1 in about 20 steps. That is why binary search over a billion items needs only about 30 comparisons." },
      { type: "code", language: "javascript", code: `function countHalvings(n) {
  let steps = 0;
  while (n > 1) {
    n = Math.floor(n / 2);
    steps++;
  }
  return steps;
}
console.log(countHalvings(1_000_000));`, output: `19` },

      { type: "h2", text: "Recursion complexity" },
      { type: "p", text: "For recursive code: **time ≈ number of calls × work per call**, and **space ≈ maximum recursion depth** (the call stack)." },
      { type: "code", language: "javascript", code: `// Each call makes 2 calls: a tree with ~2ⁿ nodes → O(2ⁿ) time, O(n) space (depth)
function fib(n) {
  if (n <= 1) return n;
  return fib(n - 1) + fib(n - 2);
}

// Memoised: each n is computed once → O(n) time, O(n) space
function fibMemo(n, memo = new Map()) {
  if (n <= 1) return n;
  if (memo.has(n)) return memo.get(n);
  const v = fibMemo(n - 1, memo) + fibMemo(n - 2, memo);
  memo.set(n, v);
  return v;
}
console.log(fibMemo(50));`, output: `12586269025` },
      { type: "table", headers: ["Pattern", "Complexity"], rows: [
        ["Halve the input, constant work (binary search)", "O(log n)"],
        ["Halve the input, linear work to split or merge (merge sort)", "O(n log n)"],
        ["Two recursive calls, input shrinks by 1 (naive fib)", "O(2ⁿ)"],
        ["Visit every node of a tree once", "O(n)"]
      ] },

      { type: "h2", text: "Space complexity" },
      { type: "ul", items: [
        "Count the **extra** memory your algorithm allocates: new arrays, maps, sets and the recursion stack.",
        "The input itself usually isn’t counted (state this assumption in interviews).",
        "Sorting in place is O(1) extra space (or O(log n) for the recursion stack of quicksort). Creating a sorted copy is O(n)."
      ] },

      { type: "h2", text: "Amortised analysis" },
      { type: "p", text: "`array.push` is usually O(1), but occasionally the array must grow, which copies everything (O(n)). Because the capacity doubles each time, the expensive copies are rare enough that the **average cost per push stays O(1)**. That is amortised O(1). The same reasoning applies to HashMap resizing." },

      { type: "h2", text: "Using constraints to choose an approach" },
      { type: "p", text: "Interview problems state input limits. Assume roughly **10⁸ simple operations per second**, and the constraint tells you which complexity is acceptable:" },
      { type: "table", headers: ["n up to", "Acceptable complexity", "Typical techniques"], rows: [
        ["10–12", "O(n!)", "Permutations, brute force"],
        ["20–25", "O(2ⁿ)", "Subsets, backtracking, bitmask DP"],
        ["~500", "O(n³)", "Triple loops, Floyd–Warshall"],
        ["~5,000", "O(n²)", "Nested loops, 2D DP"],
        ["~10⁶", "O(n log n) or O(n)", "Sorting, heaps, hashing, two pointers"],
        ["10⁹ or more", "O(log n) or O(1)", "Binary search, math"]
      ] },
      { type: "callout", variant: "tip", text: "Say this out loud in interviews: “n goes up to 10⁵, so O(n²) would be 10¹⁰ operations, which is too slow. I need O(n log n) or better.” It shows structured thinking before you write any code." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What does O(n) mean?", answer: ["The algorithm’s running time grows at most linearly with input size n, ignoring constant factors and lower-order terms. Doubling the input roughly doubles the work in the worst case."] },
      { type: "qa", question: "What is the time complexity of this code?", code: `for (let i = 1; i < n; i *= 2) {
  for (let j = 0; j < n; j++) {
    work();
  }
}`, language: "javascript", answer: ["O(n log n). The outer loop doubles `i`, so it runs about log₂ n times, and the inner loop runs n times for each of those."] },
      { type: "qa", question: "Is O(2n) faster than O(n)?", answer: ["They are the same complexity class. Big-O drops constants because it describes growth, not exact speed. In practice the constant can matter for real-world performance, but asymptotically both are linear."] },
      { type: "qa", question: "What is the space complexity of a recursive function?", answer: ["At least the maximum depth of the recursion multiplied by the space per stack frame, plus any data structures it allocates. For example, a recursive DFS on a skewed tree uses O(n) stack space, and on a balanced tree O(log n)."] },
      { type: "qa", question: "What is amortised time complexity?", answer: ["The average cost per operation over a worst-case sequence of operations. Dynamic array append is amortised O(1): an occasional O(n) resize is spread across the many cheap appends made possible by geometric growth."] },

      { type: "exercise", title: "Analyse and improve", task: "This function returns the elements that appear in both arrays. State its time complexity, then rewrite it in O(n + m).", language: "javascript", starter: `function common(a, b) {
  const result = [];
  for (const x of a) {
    if (b.includes(x) && !result.includes(x)) result.push(x);
  }
  return result;
}`, solution: `// Original: for each of n items, b.includes is O(m) and result.includes up to O(n)
// → O(n × (m + n))

function common(a, b) {
  const inB = new Set(b);          // O(m)
  const result = new Set();
  for (const x of a) {             // O(n)
    if (inB.has(x)) result.add(x); // O(1) average
  }
  return [...result];
}

console.log(common([1, 2, 2, 3, 4], [2, 4, 4, 6]));`, output: `[ 2, 4 ]`, explanation: "Converting `b` to a Set makes each membership check O(1), and a Set for the result removes duplicates in O(1). The total is O(n + m) time and O(n + m) space, the usual trade of memory for speed." },

      { type: "summary", items: [
        "Big-O describes how work grows with input size. Drop constants and lower-order terms.",
        "Sequential steps add, nested steps multiply, and watch for hidden O(n) built-in methods.",
        "Halving gives log n. Recursive cost is calls × work per call, and its space is the stack depth.",
        "Trade space for time with hashing, and know amortised O(1) for dynamic arrays.",
        "Use input constraints to work out the complexity you need before coding."
      ] }
    ]
  },

  {
    slug: "arrays-and-hashing",
    title: "Arrays, Strings and Hashing",
    summary: "The most common interview category: frequency counting, prefix sums, grouping and the hash map patterns behind them.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Arrays and strings are the most frequent inputs in coding interviews, and hash maps are the most common tool for making those problems fast. Mastering a handful of hashing patterns turns many “medium” problems into routine ones." },

      { type: "h2", text: "The mental model: a hash map is a coat check" },
      { type: "p", text: "Give the attendant your coat and get a ticket number. Later, the ticket takes them straight to the right hook, with no need to search every coat. A hash map turns a key into a “ticket” (a hash) that points directly to where the value is stored, which gives **O(1) average** lookups, inserts and deletes." },
      { type: "table", headers: ["Operation", "Array", "Hash map / set"], rows: [
        ["Access by index", "O(1)", "-"],
        ["Search for a value", "O(n)", "O(1) average"],
        ["Insert or delete at the end", "O(1) amortised", "O(1) average"],
        ["Insert or delete at the front or middle", "O(n)", "O(1) average"],
        ["Ordered iteration", "Yes", "Insertion order in JS `Map`, none in a Java `HashMap`"]
      ] },

      { type: "h2", text: "Pattern 1: frequency counting" },
      { type: "code", language: "javascript", code: `// Are two strings anagrams? ("listen", "silent")
function isAnagram(s, t) {
  if (s.length !== t.length) return false;
  const count = new Map();
  for (const ch of s) count.set(ch, (count.get(ch) || 0) + 1);
  for (const ch of t) {
    if (!count.get(ch)) return false;   // missing or already used up
    count.set(ch, count.get(ch) - 1);
  }
  return true;
}

// First non-repeating character
function firstUnique(s) {
  const freq = {};
  for (const ch of s) freq[ch] = (freq[ch] || 0) + 1;
  for (let i = 0; i < s.length; i++) if (freq[s[i]] === 1) return i;
  return -1;
}

console.log(isAnagram("listen", "silent"), firstUnique("codeazycode"));`, output: `true 4` },

      { type: "h2", text: "Pattern 2: complement lookup (Two Sum)" },
      { type: "code", language: "javascript", code: `// Return indices of the two numbers that add up to target
function twoSum(nums, target) {
  const indexOf = new Map();               // value → index
  for (let i = 0; i < nums.length; i++) {
    const need = target - nums[i];
    if (indexOf.has(need)) return [indexOf.get(need), i];
    indexOf.set(nums[i], i);
  }
  return [];
}
console.log(twoSum([2, 7, 11, 15], 9));`, output: `[ 0, 1 ]` },

      { type: "h2", text: "Pattern 3: grouping by a key" },
      { type: "code", language: "javascript", code: `// Group anagrams: ["eat","tea","tan","ate","nat","bat"]
function groupAnagrams(words) {
  const groups = new Map();
  for (const w of words) {
    const key = [...w].sort().join("");    // "eat" → "aet"
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(w);
  }
  return [...groups.values()];
}
console.log(groupAnagrams(["eat", "tea", "tan", "ate", "nat", "bat"]));`, output: `[ [ 'eat', 'tea', 'ate' ], [ 'tan', 'nat' ], [ 'bat' ] ]` },
      { type: "callout", variant: "tip", text: "Choosing the right **key** is the whole trick. Sorted letters identify anagrams, `row-col` identifies grid cells, and `(x2 - x1)/(y2 - y1)` identifies points on the same line." },

      { type: "h2", text: "Pattern 4: prefix sums" },
      { type: "p", text: "Precompute running totals so that any range sum is answered in O(1): `sum(i..j) = prefix[j + 1] - prefix[i]`." },
      { type: "code", language: "javascript", code: `function buildPrefix(nums) {
  const prefix = [0];
  for (const x of nums) prefix.push(prefix[prefix.length - 1] + x);
  return prefix;
}
const nums = [3, 1, 4, 1, 5, 9];
const prefix = buildPrefix(nums);           // [0, 3, 4, 8, 9, 14, 23]
console.log(prefix[5] - prefix[2]);         // sum of nums[2..4] = 4 + 1 + 5`, output: `10` },
      { type: "h3", text: "Prefix sum + hash map: subarray sum equals K" },
      { type: "code", language: "javascript", code: `// Count contiguous subarrays whose sum is exactly k (works with negatives)
function subarraySum(nums, k) {
  const seen = new Map([[0, 1]]);    // prefix sum → how many times seen
  let sum = 0, count = 0;
  for (const x of nums) {
    sum += x;
    count += seen.get(sum - k) || 0; // earlier prefix that leaves exactly k
    seen.set(sum, (seen.get(sum) || 0) + 1);
  }
  return count;
}
console.log(subarraySum([1, 2, 3, -2, 2], 3));`, output: `4` },
      { type: "p", text: "If the running sum is `sum` now and was `sum - k` earlier, the elements in between add up to exactly `k`. The four subarrays here are `[1, 2]`, `[3]`, `[2, 3, -2]` and `[3, -2, 2]`." },

      { type: "h2", text: "Pattern 5: Set for seen and visited" },
      { type: "code", language: "javascript", code: `// Longest consecutive sequence in O(n): [100, 4, 200, 1, 3, 2] → 4 (1,2,3,4)
function longestConsecutive(nums) {
  const set = new Set(nums);
  let best = 0;
  for (const x of set) {
    if (set.has(x - 1)) continue;       // only start counting at a sequence start
    let len = 1;
    while (set.has(x + len)) len++;
    best = Math.max(best, len);
  }
  return best;
}
console.log(longestConsecutive([100, 4, 200, 1, 3, 2]));`, output: `4` },

      { type: "h2", text: "String tips" },
      { type: "ul", items: [
        "Strings are **immutable** in JavaScript and Java. Building one with `+=` in a loop can be O(n²). Collect the parts in an array and `join` them, or use a `StringBuilder`.",
        "For lowercase-letter problems, an array of 26 counts (`new Array(26).fill(0)`, indexed by `ch.charCodeAt(0) - 97`) is faster than a map.",
        "Know `split`, `join`, `slice`, `includes`, `charCodeAt` and `localeCompare`, and that a reversal is `[...s].reverse().join(\"\")`."
      ] },

      { type: "h2", text: "In-place array techniques" },
      { type: "code", language: "javascript", code: `// Move all zeros to the end, keeping order, O(n) time and O(1) space
function moveZeros(nums) {
  let write = 0;
  for (let read = 0; read < nums.length; read++) {
    if (nums[read] !== 0) nums[write++] = nums[read];
  }
  while (write < nums.length) nums[write++] = 0;
  return nums;
}
console.log(moveZeros([0, 1, 0, 3, 12]));`, output: `[ 1, 3, 12, 0, 0 ]` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Why is hash map lookup O(1) on average but O(n) in the worst case?", answer: ["A good hash function spreads keys evenly across buckets, so each bucket holds about one entry. If many keys collide into the same bucket, because of a bad hash or adversarial input, lookups degrade to scanning that bucket: O(n) with linked chaining, or O(log n) with Java 8’s treeified buckets."] },
      { type: "qa", question: "How do you find the first duplicate in an array?", answer: ["Iterate while keeping a Set of seen values. The first value already in the set is the first duplicate. That is O(n) time and O(n) space. If the values are limited to 1..n, you can use index marking (negating values) for O(1) extra space."] },
      { type: "qa", question: "When would you use a prefix sum?", answer: ["When you need many range-sum queries on a static array (each O(1) after an O(n) build), or to count or find subarrays with a target sum by combining prefix sums with a hash map. It works even when the numbers are negative, unlike a sliding window."] },
      { type: "qa", question: "Map vs plain object in JavaScript: which for hashing?", answer: ["`Map` accepts any key type (objects, numbers kept as numbers), preserves insertion order, has `size`, and avoids prototype-key pitfalls such as \"constructor\". Objects are fine for simple string keys. In interviews, `Map` and `Set` show clear intent."] },

      { type: "exercise", title: "Top K frequent elements", task: "Given an integer array and k, return the k most frequent elements in any order. Aim for better than O(n log n) using bucket sort by frequency. Example: `[1,1,1,2,2,3]`, k = 2 → `[1, 2]`.", language: "javascript", solution: `function topKFrequent(nums, k) {
  const freq = new Map();
  for (const x of nums) freq.set(x, (freq.get(x) || 0) + 1);

  // buckets[f] = numbers that appear exactly f times
  const buckets = Array.from({ length: nums.length + 1 }, () => []);
  for (const [num, f] of freq) buckets[f].push(num);

  const result = [];
  for (let f = buckets.length - 1; f >= 0 && result.length < k; f--) {
    for (const num of buckets[f]) {
      result.push(num);
      if (result.length === k) break;
    }
  }
  return result;
}

console.log(topKFrequent([1, 1, 1, 2, 2, 3], 2));
console.log(topKFrequent([4, 4, 5, 5, 5, 6, 7, 7, 7, 7], 2));`, output: `[ 1, 2 ]
[ 7, 5 ]`, explanation: "Count the frequencies in O(n). A frequency can be at most n, so the numbers can be placed into n + 1 buckets by frequency, then read from the highest bucket down. That is O(n) total, versus O(n log n) for sorting and O(n log k) with a heap." },

      { type: "summary", items: [
        "Hash maps and sets give O(1) average lookups, trading memory for speed.",
        "Core patterns: frequency counts, complement lookup, grouping by a key, and seen/visited sets.",
        "Prefix sums answer range sums in O(1), and combined with a hash map they count subarrays with sum K.",
        "Avoid O(n²) string building, and use 26-slot count arrays for letters.",
        "In-place techniques with read and write pointers save extra space."
      ] }
    ]
  },

  {
    slug: "two-pointers",
    title: "Two Pointers and Sliding Window",
    summary: "Turn O(n²) array and string problems into O(n) by moving two indexes intelligently.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "A large share of array and string interview problems (pair sums, palindromes, longest or shortest substrings, subarrays meeting a condition) are solved with these two patterns. Recognising them quickly is one of the highest-value interview skills." },

      { type: "h2", text: "The mental model: two fingers on a ruler" },
      { type: "p", text: "Instead of comparing every pair (a nested loop), put **two fingers** on the data and move them according to what you see. Each finger only ever moves forward (or inward), so the total work is O(n). A **sliding window** is a special case: the two fingers mark the edges of a window that grows and shrinks as it slides along." },

      { type: "h2", text: "Pattern A: opposite ends, moving inward" },
      { type: "code", language: "javascript", code: `// Is it a palindrome, ignoring case and non-letters?
function isPalindrome(s) {
  const clean = s.toLowerCase().replace(/[^a-z0-9]/g, "");
  let l = 0, r = clean.length - 1;
  while (l < r) {
    if (clean[l] !== clean[r]) return false;
    l++; r--;
  }
  return true;
}
console.log(isPalindrome("A man, a plan, a canal: Panama"));`, output: `true` },
      { type: "code", language: "javascript", code: `// Container with most water: lines at each index, pick 2 to hold the most water
function maxArea(height) {
  let l = 0, r = height.length - 1, best = 0;
  while (l < r) {
    const area = Math.min(height[l], height[r]) * (r - l);
    best = Math.max(best, area);
    // move the SHORTER side: moving the taller one can never help
    height[l] < height[r] ? l++ : r--;
  }
  return best;
}
console.log(maxArea([1, 8, 6, 2, 5, 4, 8, 3, 7]));`, output: `49` },
      { type: "callout", variant: "tip", text: "Always justify the pointer move in an interview. Here, the area is limited by the shorter line, so moving the taller pointer inward can only shrink the width without raising the limit. That side can never give a better answer." },

      { type: "h2", text: "Pattern B: same direction (fast and slow)" },
      { type: "code", language: "javascript", code: `// Remove duplicates from a SORTED array in place; return the new length
function removeDuplicates(nums) {
  if (nums.length === 0) return 0;
  let slow = 0;
  for (let fast = 1; fast < nums.length; fast++) {
    if (nums[fast] !== nums[slow]) {
      slow++;
      nums[slow] = nums[fast];
    }
  }
  return slow + 1;
}
const arr = [1, 1, 2, 2, 2, 3, 4, 4];
const len = removeDuplicates(arr);
console.log(len, arr.slice(0, len));`, output: `4 [ 1, 2, 3, 4 ]` },

      { type: "h2", text: "Pattern C: 3Sum (sort + two pointers)" },
      { type: "code", language: "javascript", code: `// All unique triplets that sum to 0
function threeSum(nums) {
  nums.sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < nums.length - 2; i++) {
    if (i > 0 && nums[i] === nums[i - 1]) continue;       // skip duplicate anchors
    let l = i + 1, r = nums.length - 1;
    while (l < r) {
      const sum = nums[i] + nums[l] + nums[r];
      if (sum === 0) {
        result.push([nums[i], nums[l], nums[r]]);
        while (l < r && nums[l] === nums[l + 1]) l++;     // skip duplicates
        while (l < r && nums[r] === nums[r - 1]) r--;
        l++; r--;
      } else if (sum < 0) l++;
      else r--;
    }
  }
  return result;
}
console.log(threeSum([-1, 0, 1, 2, -1, -4]));`, output: `[ [ -1, -1, 2 ], [ -1, 0, 1 ] ]` },

      { type: "h2", text: "Sliding window: fixed size" },
      { type: "code", language: "javascript", code: `// Maximum sum of any k consecutive elements
function maxSumK(nums, k) {
  let window = 0;
  for (let i = 0; i < k; i++) window += nums[i];
  let best = window;
  for (let i = k; i < nums.length; i++) {
    window += nums[i] - nums[i - k];     // slide: add the new element, drop the oldest
    best = Math.max(best, window);
  }
  return best;
}
console.log(maxSumK([2, 1, 5, 1, 3, 2], 3));`, output: `9` },

      { type: "h2", text: "Sliding window: variable size" },
      { type: "p", text: "The template: **expand** the right edge every step, and **shrink** the left edge while the window is invalid. Record the answer whenever the window is valid." },
      { type: "code", language: "javascript", code: `// Longest substring without repeating characters
function lengthOfLongestSubstring(s) {
  const lastIndex = new Map();
  let left = 0, best = 0;
  for (let right = 0; right < s.length; right++) {
    const ch = s[right];
    if (lastIndex.has(ch) && lastIndex.get(ch) >= left) {
      left = lastIndex.get(ch) + 1;          // jump past the duplicate
    }
    lastIndex.set(ch, right);
    best = Math.max(best, right - left + 1);
  }
  return best;
}
console.log(lengthOfLongestSubstring("abcabcbb"), lengthOfLongestSubstring("pwwkew"));`, output: `3 3` },
      { type: "code", language: "javascript", code: `// Smallest subarray with sum >= target (positive numbers)
function minSubArrayLen(target, nums) {
  let left = 0, sum = 0, best = Infinity;
  for (let right = 0; right < nums.length; right++) {
    sum += nums[right];                     // expand
    while (sum >= target) {                 // valid → try to shrink
      best = Math.min(best, right - left + 1);
      sum -= nums[left++];
    }
  }
  return best === Infinity ? 0 : best;
}
console.log(minSubArrayLen(7, [2, 3, 1, 2, 4, 3]));`, output: `2` },
      { type: "callout", variant: "warning", text: "A variable-size window on sums only works when all numbers are **non-negative**, because shrinking must always decrease the sum. With negative numbers, use prefix sums with a hash map instead." },

      { type: "h2", text: "Recognising the pattern" },
      { type: "table", headers: ["Clue in the problem", "Likely pattern"], rows: [
        ["Sorted array, find a pair or triplet with a sum", "Two pointers from both ends"],
        ["Palindrome, reverse, compare from both ends", "Two pointers from both ends"],
        ["Remove or partition in place", "Fast and slow pointers"],
        ["“Contiguous subarray or substring” plus longest, shortest or max", "Sliding window"],
        ["“Exactly K distinct” or “at most K”", "Sliding window with a count map"],
        ["Linked list cycle or middle node", "Fast and slow pointers"]
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "When can you use two pointers instead of nested loops?", answer: ["When the data has an order or structure that tells you which pointer to move. For example, in a sorted array a sum that is too small means move left forward, and too big means move right back. Each move discards possibilities that can’t be the answer, so every element is visited at most a constant number of times, giving O(n)."] },
      { type: "qa", question: "What is the difference between fixed and variable sliding windows?", answer: ["A fixed window has a constant size k and slides by adding one element and removing one. A variable window expands its right edge each step and shrinks its left edge while a constraint is violated (or while it is still satisfied, for minimum-length problems), tracking the best valid window."] },
      { type: "qa", question: "Why doesn’t a sliding window work for subarray sums with negative numbers?", answer: ["Shrinking the window assumes that removing elements makes the sum smaller and adding makes it larger. Negative numbers break this, so a window that is too big might become valid by adding more elements. Use prefix sums with a hash map instead."] },

      { type: "exercise", title: "Longest substring with at most K distinct characters", task: "Return the length of the longest substring containing at most k distinct characters. Example: s = \"eceba\", k = 2 → 3 (\"ece\").", language: "javascript", solution: `function longestKDistinct(s, k) {
  const count = new Map();
  let left = 0, best = 0;
  for (let right = 0; right < s.length; right++) {
    count.set(s[right], (count.get(s[right]) || 0) + 1);      // expand

    while (count.size > k) {                                   // invalid → shrink
      const ch = s[left++];
      count.set(ch, count.get(ch) - 1);
      if (count.get(ch) === 0) count.delete(ch);
    }

    best = Math.max(best, right - left + 1);                   // valid window
  }
  return best;
}

console.log(longestKDistinct("eceba", 2), longestKDistinct("aaabbccd", 2));`, output: `3 5`, explanation: "The map tracks the characters in the window and their counts, and `count.size` is the number of distinct characters. Each character enters and leaves the window at most once, so it runs in O(n) time with O(k) space." },

      { type: "summary", items: [
        "Two pointers replace nested loops by moving indexes based on what they see.",
        "Opposite ends suit sorted pairs and palindromes. Fast and slow pointers suit in-place edits and cycles.",
        "Sort first to unlock two pointers (3Sum), and skip duplicates carefully.",
        "Sliding window: expand right, shrink left while invalid, and record the best.",
        "Use prefix sums instead of a window when values can be negative."
      ] }
    ]
  },

  {
    slug: "binary-search",
    title: "Binary Search",
    summary: "The classic algorithm, boundary-finding variants, rotated arrays and “binary search on the answer”.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Binary search finds an item among a billion in about 30 steps. Beyond sorted arrays, its real power is the “binary search on the answer” technique, which solves many optimisation problems that don’t look like search problems at all. It is also notorious for off-by-one bugs." },

      { type: "h2", text: "The mental model: guess the number" },
      { type: "p", text: "“I’m thinking of a number from 1 to 100.” The best strategy is to guess 50. “Higher.” Guess 75. Each guess eliminates **half** of the remaining possibilities, so you need at most about 7 guesses. Binary search works whenever you can ask a question that tells you which half the answer is in." },

      { type: "h2", text: "The classic version" },
      { type: "code", language: "javascript", code: `function binarySearch(nums, target) {
  let lo = 0, hi = nums.length - 1;           // inclusive range [lo, hi]
  while (lo <= hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (nums[mid] === target) return mid;
    if (nums[mid] < target) lo = mid + 1;     // answer is to the right
    else hi = mid - 1;                        // answer is to the left
  }
  return -1;
}
console.log(binarySearch([2, 5, 8, 12, 16, 23, 38, 56], 23));`, output: `5` },
      { type: "callout", variant: "note", text: "`lo + (hi - lo) / 2` instead of `(lo + hi) / 2` avoids integer overflow in languages like Java and C++ when the indexes are large. It is a habit worth keeping." },

      { type: "h2", text: "Finding boundaries: first and last occurrence" },
      { type: "p", text: "Many problems need the **first** position where something becomes true. Use a half-open range `[lo, hi)` and never return early:" },
      { type: "code", language: "javascript", code: `// First index where nums[i] >= target (also called lower bound)
function lowerBound(nums, target) {
  let lo = 0, hi = nums.length;               // half-open [lo, hi)
  while (lo < hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (nums[mid] < target) lo = mid + 1;
    else hi = mid;                            // mid might be the answer: keep it
  }
  return lo;                                   // nums.length if none
}

function firstAndLast(nums, target) {
  const first = lowerBound(nums, target);
  if (first === nums.length || nums[first] !== target) return [-1, -1];
  const last = lowerBound(nums, target + 1) - 1;
  return [first, last];
}

console.log(firstAndLast([5, 7, 7, 8, 8, 8, 10], 8));`, output: `[ 3, 5 ]` },

      { type: "h2", text: "The universal template: first true" },
      { type: "p", text: "Whenever a condition is `false, false, …, false, true, true, …, true` across a range (we call this **monotonic**), you can binary search for the first `true`:" },
      { type: "code", language: "javascript", code: `// Smallest x in [lo, hi] where isOk(x) is true (assumes isOk(hi) is true)
function firstTrue(lo, hi, isOk) {
  while (lo < hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (isOk(mid)) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

// Integer square root: largest x with x*x <= n → first x with x*x > n, minus 1
const isqrt = (n) => firstTrue(0, n + 1, (x) => x * x > n) - 1;
console.log(isqrt(17), isqrt(64));`, output: `4 8` },

      { type: "h2", text: "Binary search on the answer" },
      { type: "p", text: "Some problems ask for the **minimum value that works**, such as the smallest capacity, speed or time. If “value v works” means every larger value also works, binary search over the possible values and test each guess with a simple check:" },
      { type: "code", language: "javascript", code: `// Koko eats bananas: piles of bananas, h hours. Find the minimum eating speed k
// (bananas per hour, one pile per hour at most) to finish within h hours.
function minEatingSpeed(piles, h) {
  const hoursAt = (k) => piles.reduce((sum, p) => sum + Math.ceil(p / k), 0);
  let lo = 1, hi = Math.max(...piles);
  while (lo < hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (hoursAt(mid) <= h) hi = mid;        // fast enough → try slower
    else lo = mid + 1;                       // too slow → go faster
  }
  return lo;
}
console.log(minEatingSpeed([3, 6, 7, 11], 8));`, output: `4` },
      { type: "table", headers: ["Problem", "Search space", "Check for guess v"], rows: [
        ["Koko eating bananas", "Speed 1..max(pile)", "Hours needed at speed v ≤ h?"],
        ["Ship packages within D days", "Capacity max(weight)..sum(weights)", "Days needed with capacity v ≤ D?"],
        ["Split array, minimise the largest sum", "max(nums)..sum(nums)", "Can we split into ≤ m parts with each sum ≤ v?"],
        ["Minimum days to make m bouquets", "Days 1..max(bloomDay)", "Bouquets possible by day v ≥ m?"]
      ] },

      { type: "h2", text: "Rotated sorted array" },
      { type: "code", language: "javascript", code: `// [4,5,6,7,0,1,2] is sorted, then rotated. Find target in O(log n).
function searchRotated(nums, target) {
  let lo = 0, hi = nums.length - 1;
  while (lo <= hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (nums[mid] === target) return mid;
    if (nums[lo] <= nums[mid]) {                       // left half is sorted
      if (nums[lo] <= target && target < nums[mid]) hi = mid - 1;
      else lo = mid + 1;
    } else {                                           // right half is sorted
      if (nums[mid] < target && target <= nums[hi]) lo = mid + 1;
      else hi = mid - 1;
    }
  }
  return -1;
}
console.log(searchRotated([4, 5, 6, 7, 0, 1, 2], 0));`, output: `4` },

      { type: "h2", text: "Avoiding off-by-one bugs" },
      { type: "table", headers: ["Style", "Loop", "Shrink", "Use for"], rows: [
        ["Inclusive `[lo, hi]`", "`while (lo <= hi)`", "`lo = mid + 1`, `hi = mid - 1`", "Find an exact value"],
        ["Half-open `[lo, hi)`", "`while (lo < hi)`", "`lo = mid + 1`, `hi = mid`", "Find a boundary (first true, lower bound)"]
      ] },
      { type: "ul", items: [
        "Pick one style per problem and stick to it.",
        "Test the edge cases: empty array, one element, target smaller than all, target larger than all.",
        "Make sure every iteration shrinks the range, otherwise you get an infinite loop."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What are the requirements for binary search?", answer: ["The search space must be ordered so each comparison eliminates one half: a sorted array, or more generally a monotonic condition that is false for all values before some point and true after it. Random access (O(1) indexing) is needed for O(log n) on arrays."] },
      { type: "qa", question: "What is “binary search on the answer”?", answer: ["When asked for the minimum or maximum value satisfying a condition, and feasibility is monotonic (if v works, every larger v also works), binary search over the range of possible values and check each candidate with a feasibility function. The total cost is O(log(range) × cost of the check)."] },
      { type: "qa", question: "How do you find the first occurrence of a target in a sorted array with duplicates?", answer: ["Use the lower-bound variant: on a match or anything larger, move `hi = mid` (keeping mid as a candidate), otherwise `lo = mid + 1`. When `lo === hi`, check that `nums[lo]` equals the target."] },
      { type: "qa", question: "How do you find the minimum in a rotated sorted array?", answer: ["Compare `nums[mid]` with `nums[hi]`. If `nums[mid] > nums[hi]`, the minimum is to the right (`lo = mid + 1`). Otherwise it is at mid or to the left (`hi = mid`). It ends when `lo === hi`, in O(log n)."] },

      { type: "exercise", title: "Ship packages within D days", task: "Packages with given weights must be shipped in order within `days` days. Each day the ship carries a contiguous run of packages up to its capacity. Return the minimum capacity. Example: weights = [1..10], days = 5 → 15.", language: "javascript", solution: `function shipWithinDays(weights, days) {
  const daysNeeded = (cap) => {
    let d = 1, load = 0;
    for (const w of weights) {
      if (load + w > cap) { d++; load = 0; }   // start a new day
      load += w;
    }
    return d;
  };

  let lo = Math.max(...weights);                 // must fit the heaviest package
  let hi = weights.reduce((a, b) => a + b, 0);   // everything in one day
  while (lo < hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (daysNeeded(mid) <= days) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

console.log(shipWithinDays([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 5));
console.log(shipWithinDays([3, 2, 2, 4, 1, 4], 3));`, output: `15
6`, explanation: "A larger capacity never needs more days, so feasibility is monotonic. The answer lies between the heaviest single package and the total weight. Each check is a greedy O(n) simulation, so the total is O(n log(sum))." },

      { type: "summary", items: [
        "Binary search halves the search space each step: O(log n).",
        "Use the inclusive template for exact matches and the half-open template for boundaries.",
        "“First true” over a monotonic condition is the universal pattern.",
        "Binary search on the answer solves “minimum capacity, speed or time” problems.",
        "Handle rotated arrays by finding which half is sorted, and test the edge cases."
      ] }
    ]
  },

  {
    slug: "stacks-queues-linked-lists",
    title: "Stacks, Queues and Linked Lists",
    summary: "Linear structures and their signature problems: valid parentheses, monotonic stacks, BFS queues and linked list pointer tricks.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Stacks power undo buttons, browser history, expression parsing and recursion itself. Queues power task scheduling and BFS. Linked lists test careful pointer manipulation. Each has a set of famous interview problems that appear again and again." },

      { type: "h2", text: "The mental models" },
      { type: "table", headers: ["Structure", "Analogy", "Order", "Core operations"], rows: [
        ["Stack", "A pile of plates", "LIFO: last in, first out", "push, pop, peek: O(1)"],
        ["Queue", "A ticket line", "FIFO: first in, first out", "enqueue, dequeue: O(1)"],
        ["Deque", "A line where you can join or leave at either end", "Both ends", "push and pop at both ends: O(1)"],
        ["Linked list", "A treasure hunt where each clue points to the next", "Sequential", "Insert or delete at a known node: O(1). Search: O(n)"]
      ] },

      { type: "h2", text: "Stacks in JavaScript" },
      { type: "code", language: "javascript", code: `const stack = [];
stack.push(1); stack.push(2); stack.push(3);
console.log(stack.pop(), stack[stack.length - 1]);   // pop, then peek`, output: `3 2` },
      { type: "h3", text: "Valid parentheses" },
      { type: "code", language: "javascript", code: `function isValid(s) {
  const pairs = { ")": "(", "]": "[", "}": "{" };
  const stack = [];
  for (const ch of s) {
    if (ch in pairs) {
      if (stack.pop() !== pairs[ch]) return false;   // wrong or missing opener
    } else {
      stack.push(ch);
    }
  }
  return stack.length === 0;                          // nothing left unclosed
}
console.log(isValid("({[]})"), isValid("([)]"), isValid("(("));`, output: `true false false` },

      { type: "h2", text: "Monotonic stack: next greater element" },
      { type: "p", text: "Keep the stack **sorted** (here decreasing). When a new element is larger than the top, it is the “next greater” element for everything it pops. Each element is pushed and popped once, so this is O(n) instead of O(n²):" },
      { type: "code", language: "javascript", code: `// For each day, how many days until a warmer temperature?
function dailyTemperatures(temps) {
  const answer = new Array(temps.length).fill(0);
  const stack = [];                        // indexes, temperatures decreasing
  for (let i = 0; i < temps.length; i++) {
    while (stack.length && temps[i] > temps[stack[stack.length - 1]]) {
      const j = stack.pop();
      answer[j] = i - j;
    }
    stack.push(i);
  }
  return answer;
}
console.log(dailyTemperatures([73, 74, 75, 71, 69, 72, 76, 73]));`, output: `[
  1, 1, 4, 2,
  1, 1, 0, 0
]` },
      { type: "callout", variant: "tip", text: "Clues for a monotonic stack: “next greater or smaller element”, “days until warmer”, “largest rectangle in a histogram”, “stock span” and “trapping rain water”." },

      { type: "h2", text: "Queues" },
      { type: "code", language: "javascript", code: `// array.shift() is O(n), which is slow for big queues.
// Use a head pointer, or a proper deque implementation.
class Queue {
  constructor() { this.items = []; this.head = 0; }
  enqueue(x) { this.items.push(x); }
  dequeue() { return this.items[this.head++]; }
  get size() { return this.items.length - this.head; }
}

const q = new Queue();
q.enqueue("A"); q.enqueue("B"); q.enqueue("C");
console.log(q.dequeue(), q.dequeue(), q.size);`, output: `A B 1` },
      { type: "p", text: "Queues are at the heart of **BFS** (see the Graphs lesson), task schedulers, rate limiters and producer–consumer systems." },

      { type: "h2", text: "Linked lists" },
      { type: "code", language: "javascript", code: `class ListNode {
  constructor(val, next = null) {
    this.val = val;
    this.next = next;
  }
}

const fromArray = (arr) => arr.reduceRight((next, v) => new ListNode(v, next), null);
const toArray = (head) => { const out = []; while (head) { out.push(head.val); head = head.next; } return out; };

const list = fromArray([1, 2, 3, 4, 5]);
console.log(toArray(list));`, output: `[ 1, 2, 3, 4, 5 ]` },
      { type: "h3", text: "Reverse a linked list" },
      { type: "code", language: "javascript", code: `function reverseList(head) {
  let prev = null, curr = head;
  while (curr) {
    const next = curr.next;   // 1. remember the rest
    curr.next = prev;         // 2. flip the pointer
    prev = curr;              // 3. advance prev
    curr = next;              // 4. advance curr
  }
  return prev;
}
console.log(toArray(reverseList(fromArray([1, 2, 3, 4, 5]))));`, output: `[ 5, 4, 3, 2, 1 ]` },
      { type: "h3", text: "Fast and slow pointers" },
      { type: "code", language: "javascript", code: `// Middle node: fast moves 2 steps, slow moves 1
function middle(head) {
  let slow = head, fast = head;
  while (fast && fast.next) {
    slow = slow.next;
    fast = fast.next.next;
  }
  return slow.val;
}

// Cycle detection (Floyd): if there's a loop, fast eventually laps slow
function hasCycle(head) {
  let slow = head, fast = head;
  while (fast && fast.next) {
    slow = slow.next;
    fast = fast.next.next;
    if (slow === fast) return true;
  }
  return false;
}

const cyclic = fromArray([1, 2, 3]);
cyclic.next.next.next = cyclic.next;          // 3 → 2 creates a loop
console.log(middle(fromArray([1, 2, 3, 4, 5])), hasCycle(cyclic), hasCycle(fromArray([1, 2])));`, output: `3 true false` },
      { type: "h3", text: "The dummy node trick" },
      { type: "code", language: "javascript", code: `// Merge two sorted lists. A dummy head avoids special-casing the first node.
function mergeTwoLists(a, b) {
  const dummy = new ListNode(0);
  let tail = dummy;
  while (a && b) {
    if (a.val <= b.val) { tail.next = a; a = a.next; }
    else { tail.next = b; b = b.next; }
    tail = tail.next;
  }
  tail.next = a || b;
  return dummy.next;
}
console.log(toArray(mergeTwoLists(fromArray([1, 3, 5]), fromArray([2, 4, 6, 8]))));`, output: `[
  1, 2, 3, 4,
  5, 6, 8
]` },

      { type: "h2", text: "Array vs linked list" },
      { type: "table", headers: ["", "Array", "Linked list"], rows: [
        ["Access the i-th element", "O(1)", "O(n)"],
        ["Insert or delete at the front", "O(n)", "O(1)"],
        ["Insert or delete at a known node", "O(n) (shifting)", "O(1)"],
        ["Memory", "Contiguous and cache-friendly", "Extra pointer per node, scattered"],
        ["Used for", "Most things", "LRU caches (with a hash map), queues, undo history"]
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "How would you implement a queue using two stacks?", answer: ["Push new items onto an `in` stack. To dequeue, if the `out` stack is empty, pop everything from `in` onto `out` (which reverses the order), then pop from `out`. Each element moves at most twice, so operations are amortised O(1)."] },
      { type: "qa", question: "How do you detect a cycle in a linked list, and find where it starts?", answer: ["Floyd’s algorithm: slow moves 1 step and fast moves 2, and if they meet, there is a cycle. To find the start, reset one pointer to the head and move both one step at a time. They meet at the cycle’s first node. O(n) time, O(1) space."] },
      { type: "qa", question: "What is a monotonic stack?", answer: ["A stack kept in increasing or decreasing order: before pushing, pop every element that would break the order. Popped elements have just found their next greater (or smaller) element. It solves “next greater element” style problems in O(n)."] },
      { type: "qa", question: "Why use a dummy node in linked list problems?", answer: ["It gives a fixed node before the real head, so inserting, deleting or building at the front uses the same code as everywhere else. That removes special cases for an empty list or a changing head. Return `dummy.next` at the end."] },
      { type: "qa", question: "Design a stack that returns the minimum in O(1).", answer: ["Keep a second stack of current minimums. On push, push `min(value, currentMin)` onto it, and on pop, pop both stacks. `getMin` is then the top of the min-stack, with all operations O(1)."] },

      { type: "exercise", title: "Evaluate Reverse Polish Notation", task: "Evaluate an expression in postfix notation, for example `[\"2\",\"1\",\"+\",\"3\",\"*\"]` = (2 + 1) × 3 = 9. Support + - * /, where division truncates toward zero.", language: "javascript", solution: `function evalRPN(tokens) {
  const stack = [];
  const ops = {
    "+": (a, b) => a + b,
    "-": (a, b) => a - b,
    "*": (a, b) => a * b,
    "/": (a, b) => Math.trunc(a / b)
  };
  for (const t of tokens) {
    if (t in ops) {
      const b = stack.pop();         // second operand is on top
      const a = stack.pop();
      stack.push(ops[t](a, b));
    } else {
      stack.push(Number(t));
    }
  }
  return stack.pop();
}

console.log(evalRPN(["2", "1", "+", "3", "*"]));
console.log(evalRPN(["4", "13", "5", "/", "+"]));
console.log(evalRPN(["10", "6", "9", "3", "+", "-11", "*", "/", "*", "17", "+", "5", "+"]));`, output: `9
6
22`, explanation: "Numbers are pushed. An operator pops two operands, and the order matters: the top of the stack is the **right** operand. It then pushes the result. That is O(n) time and O(n) space, and the same idea underlies calculators and compilers." },

      { type: "summary", items: [
        "A stack is LIFO (undo, parsing, DFS) and a queue is FIFO (BFS, scheduling). Avoid `shift()` for large queues.",
        "A monotonic stack solves next-greater and histogram problems in O(n).",
        "Linked list skills: reversing with prev, curr and next, fast and slow pointers, and dummy nodes.",
        "Floyd’s algorithm detects cycles in O(1) space.",
        "Know the trade-offs between arrays and linked lists."
      ] }
    ]
  },

  {
    slug: "trees-and-recursion",
    title: "Trees and Recursion",
    summary: "Think recursively, traverse trees four ways, master BST operations, and use heaps for top-K problems.",
    readTime: 15,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Trees are everywhere: the DOM, file systems, org charts, database indexes (B-trees) and JSON. Tree problems are interview staples because they test recursion, the most important problem-solving skill in DSA." },

      { type: "h2", text: "The mental model: trust the recursion" },
      { type: "p", text: "Recursion is **delegation**. To count the people in an organisation, a CEO doesn’t count everyone. They ask each direct report, “how many people are in your team, including you?”, add them up and add 1. Each manager does the same. You only need to define:" },
      { type: "ol", items: [
        "**Base case:** the simplest input with a direct answer (an empty tree has 0 nodes).",
        "**Recursive case:** how to combine the answers for smaller pieces (left + right + 1).",
        "**Trust:** assume the recursive call returns the right answer for the smaller piece. Don’t trace every level in your head."
      ] },
      { type: "code", language: "javascript", code: `class TreeNode {
  constructor(val, left = null, right = null) {
    this.val = val; this.left = left; this.right = right;
  }
}

//        8
//      /   \\
//     3     10
//    / \\      \\
//   1   6      14
const root = new TreeNode(8,
  new TreeNode(3, new TreeNode(1), new TreeNode(6)),
  new TreeNode(10, null, new TreeNode(14))
);

const countNodes = (node) => (node ? 1 + countNodes(node.left) + countNodes(node.right) : 0);
const maxDepth = (node) => (node ? 1 + Math.max(maxDepth(node.left), maxDepth(node.right)) : 0);

console.log(countNodes(root), maxDepth(root));`, output: `6 3` },

      { type: "h2", text: "The four traversals" },
      { type: "code", language: "javascript", code: `const preorder = (n, out = []) => { if (n) { out.push(n.val); preorder(n.left, out); preorder(n.right, out); } return out; };
const inorder  = (n, out = []) => { if (n) { inorder(n.left, out); out.push(n.val); inorder(n.right, out); } return out; };
const postorder = (n, out = []) => { if (n) { postorder(n.left, out); postorder(n.right, out); out.push(n.val); } return out; };

function levelOrder(root) {
  if (!root) return [];
  const result = [], queue = [root];
  while (queue.length) {
    const levelSize = queue.length, level = [];
    for (let i = 0; i < levelSize; i++) {
      const node = queue.shift();
      level.push(node.val);
      if (node.left) queue.push(node.left);
      if (node.right) queue.push(node.right);
    }
    result.push(level);
  }
  return result;
}

console.log("pre ", preorder(root));
console.log("in  ", inorder(root));
console.log("post", postorder(root));
console.log("bfs ", levelOrder(root));`, output: `pre  [ 8, 3, 1, 6, 10, 14 ]
in   [ 1, 3, 6, 8, 10, 14 ]
post [ 1, 6, 3, 14, 10, 8 ]
bfs  [ [ 8 ], [ 3, 10 ], [ 1, 6, 14 ] ]` },
      { type: "table", headers: ["Traversal", "Order", "Typical use"], rows: [
        ["Preorder", "Node, left, right", "Copying or serialising a tree"],
        ["Inorder", "Left, node, right", "BST → sorted order"],
        ["Postorder", "Left, right, node", "Computing from children first: heights, deleting, sizes"],
        ["Level order (BFS)", "Level by level", "Shortest depth, views by level, connecting levels"]
      ] },

      { type: "h2", text: "Two styles of tree recursion" },
      { type: "h3", text: "1. Return values upward (bottom-up)" },
      { type: "code", language: "javascript", code: `// Is the tree height-balanced? Return the height, or -1 if unbalanced.
function isBalanced(root) {
  const height = (node) => {
    if (!node) return 0;
    const l = height(node.left);
    if (l === -1) return -1;
    const r = height(node.right);
    if (r === -1 || Math.abs(l - r) > 1) return -1;
    return 1 + Math.max(l, r);
  };
  return height(root) !== -1;
}
console.log(isBalanced(root));`, output: `true` },
      { type: "h3", text: "2. Pass state downward (top-down)" },
      { type: "code", language: "javascript", code: `// Root-to-leaf paths
function paths(node, path = [], out = []) {
  if (!node) return out;
  path.push(node.val);
  if (!node.left && !node.right) out.push(path.join("→"));
  paths(node.left, path, out);
  paths(node.right, path, out);
  path.pop();                         // backtrack
  return out;
}
console.log(paths(root));`, output: `[ '8→3→1', '8→3→6', '8→10→14' ]` },

      { type: "h2", text: "Binary search trees" },
      { type: "p", text: "In a BST, every node’s left subtree holds smaller values and its right subtree holds larger values, so search, insert and delete take **O(h)**. That is O(log n) when balanced and O(n) when skewed, like a linked list." },
      { type: "code", language: "javascript", code: `function bstSearch(node, target) {
  while (node && node.val !== target) {
    node = target < node.val ? node.left : node.right;
  }
  return node !== null;
}

// Validate a BST: every node must sit inside a (min, max) range from its ancestors
function isValidBST(node, min = -Infinity, max = Infinity) {
  if (!node) return true;
  if (node.val <= min || node.val >= max) return false;
  return isValidBST(node.left, min, node.val) && isValidBST(node.right, node.val, max);
}

console.log(bstSearch(root, 6), bstSearch(root, 7), isValidBST(root));`, output: `true false true` },
      { type: "callout", variant: "warning", text: "A classic bug: checking only `node.left.val < node.val < node.right.val`. A node deep in the left subtree must also be smaller than every ancestor it sits to the left of. That is why the (min, max) range is passed down." },

      { type: "h2", text: "Lowest common ancestor" },
      { type: "code", language: "javascript", code: `// For any binary tree: the first node where p and q split into different subtrees
function lca(node, p, q) {
  if (!node || node.val === p || node.val === q) return node;
  const left = lca(node.left, p, q);
  const right = lca(node.right, p, q);
  if (left && right) return node;      // found one on each side
  return left || right;
}
console.log(lca(root, 1, 6).val, lca(root, 1, 14).val);`, output: `3 8` },

      { type: "h2", text: "Heaps and priority queues" },
      { type: "p", text: "A **heap** is a complete binary tree stored in an array where each parent is smaller (min-heap) or larger (max-heap) than its children. It gives you the minimum in O(1) and inserts or removals in O(log n), which is perfect for “top K”, “k-th largest”, merging sorted lists and Dijkstra’s algorithm." },
      { type: "code", language: "javascript", code: `class MinHeap {
  constructor() { this.a = []; }
  get size() { return this.a.length; }
  peek() { return this.a[0]; }
  push(x) {
    const a = this.a; a.push(x);
    let i = a.length - 1;
    while (i > 0) {                              // bubble up
      const p = (i - 1) >> 1;
      if (a[p] <= a[i]) break;
      [a[p], a[i]] = [a[i], a[p]]; i = p;
    }
  }
  pop() {
    const a = this.a, top = a[0], last = a.pop();
    if (a.length) {
      a[0] = last;
      let i = 0;
      while (true) {                             // sink down
        const l = 2 * i + 1, r = l + 1;
        let m = i;
        if (l < a.length && a[l] < a[m]) m = l;
        if (r < a.length && a[r] < a[m]) m = r;
        if (m === i) break;
        [a[m], a[i]] = [a[i], a[m]]; i = m;
      }
    }
    return top;
  }
}

// k-th largest: keep a min-heap of size k, so its top is the answer
function kthLargest(nums, k) {
  const heap = new MinHeap();
  for (const x of nums) {
    heap.push(x);
    if (heap.size > k) heap.pop();
  }
  return heap.peek();
}
console.log(kthLargest([3, 2, 1, 5, 6, 4], 2));`, output: `5` },
      { type: "callout", variant: "note", text: "JavaScript has no built-in heap, so be ready to write one, as above. Java has `PriorityQueue` and Python has `heapq`. In interviews you can usually say “assume a priority queue” and explain the operations’ complexity." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between a binary tree and a binary search tree?", answer: ["A binary tree only requires that each node has at most two children. A BST adds an ordering invariant: all values in a node’s left subtree are smaller and all in its right subtree are larger, which enables O(h) search and makes an inorder traversal sorted."] },
      { type: "qa", question: "What is the time and space complexity of tree traversals?", answer: ["O(n) time, because each node is visited once. Space is O(h) for recursive DFS (the call stack), which is O(log n) for balanced trees and O(n) for skewed ones, and O(w) for BFS, where w is the maximum width (up to about n/2)."] },
      { type: "qa", question: "How do you convert recursion to iteration?", answer: ["Use an explicit stack to simulate the call stack. For example, iterative inorder: push left children until null, pop a node and visit it, then move to its right child. BFS naturally uses a queue."] },
      { type: "qa", question: "When would you use a heap?", answer: ["When you repeatedly need the minimum or maximum of a changing collection: top-K elements, k-th largest, merging k sorted lists, scheduling by priority, the running median (two heaps) and shortest paths with Dijkstra. Push and pop are O(log n) and peek is O(1)."] },
      { type: "qa", question: "Why does a BST degrade to O(n)?", answer: ["Inserting sorted data creates a skewed tree, effectively a linked list, with height n. Self-balancing trees (AVL, red-black) keep the height at O(log n) through rotations. Java’s TreeMap uses a red-black tree."] },

      { type: "exercise", title: "Diameter of a binary tree", task: "Return the length (in edges) of the longest path between any two nodes. The path may not pass through the root. For the example tree above, the answer is 4 (1→3→8→10→14).", language: "javascript", solution: `function diameter(root) {
  let best = 0;
  const depth = (node) => {
    if (!node) return 0;
    const l = depth(node.left);
    const r = depth(node.right);
    best = Math.max(best, l + r);      // longest path THROUGH this node
    return 1 + Math.max(l, r);         // height for the parent to use
  };
  depth(root);
  return best;
}

console.log(diameter(root));`, output: `4`, explanation: "This is the classic “return one thing, track another” pattern. Each call returns its height to its parent, but also updates a global best with `left + right`, the longest path that bends at this node. It visits each node once: O(n) time and O(h) space." },

      { type: "summary", items: [
        "Recursion means a base case, a recursive case, and trust that the smaller calls are correct.",
        "Know preorder, inorder, postorder and level-order traversals, and when to use each.",
        "Return values upward for computations, and pass state downward for paths, with backtracking.",
        "BST operations are O(h). Validate with min/max ranges, not just parent–child checks.",
        "Heaps give O(log n) access to the min or max, which makes them the tool for top-K and priority problems."
      ] }
    ]
  },

  {
    slug: "bfs-and-dfs",
    title: "Graphs: BFS, DFS and Beyond",
    summary: "Represent graphs, traverse them with BFS and DFS, solve grid problems, topological sort and shortest paths.",
    readTime: 15,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Social networks, maps, course prerequisites, package dependencies and microservice calls are all graphs. Graph problems are common in interviews at product companies, and many “grid” problems are graph problems in disguise." },

      { type: "h2", text: "The mental model: cities and roads" },
      { type: "p", text: "A graph is a set of **cities** (nodes or vertices) connected by **roads** (edges). Roads can be one-way (directed) or two-way (undirected), and may have distances (weighted). **BFS** explores like ripples in a pond, nearest cities first. **DFS** explores like a maze runner, going as deep as possible before backtracking." },

      { type: "h2", text: "Representing a graph" },
      { type: "code", language: "javascript", code: `// Edge list → adjacency list (the usual choice)
const edges = [["A", "B"], ["A", "C"], ["B", "D"], ["C", "D"], ["D", "E"]];

function buildGraph(edges, directed = false) {
  const graph = new Map();
  for (const [u, v] of edges) {
    if (!graph.has(u)) graph.set(u, []);
    if (!graph.has(v)) graph.set(v, []);
    graph.get(u).push(v);
    if (!directed) graph.get(v).push(u);
  }
  return graph;
}

const graph = buildGraph(edges);
console.log(graph.get("D"));`, output: `[ 'B', 'C', 'E' ]` },
      { type: "table", headers: ["Representation", "Space", "Check edge u→v", "List neighbours", "Best for"], rows: [
        ["Adjacency list", "O(V + E)", "O(degree)", "O(degree)", "Most graphs (sparse)"],
        ["Adjacency matrix", "O(V²)", "O(1)", "O(V)", "Dense graphs, small V"],
        ["Edge list", "O(E)", "O(E)", "O(E)", "Kruskal’s MST, input format"]
      ] },

      { type: "h2", text: "BFS: shortest path in unweighted graphs" },
      { type: "code", language: "javascript", code: `function shortestPath(graph, start, goal) {
  const queue = [[start, 0]];
  const visited = new Set([start]);           // mark when ENQUEUING
  let head = 0;
  while (head < queue.length) {
    const [node, dist] = queue[head++];
    if (node === goal) return dist;
    for (const next of graph.get(node)) {
      if (!visited.has(next)) {
        visited.add(next);
        queue.push([next, dist + 1]);
      }
    }
  }
  return -1;
}
console.log(shortestPath(graph, "A", "E"));`, output: `3` },
      { type: "callout", variant: "tip", text: "BFS finds the shortest path (fewest edges) because it explores all nodes at distance 1, then all at distance 2, and so on. Mark nodes as visited when you **enqueue** them, not when you dequeue, to avoid adding the same node many times." },

      { type: "h2", text: "DFS: explore everything" },
      { type: "code", language: "javascript", code: `function dfs(graph, start) {
  const visited = new Set();
  const order = [];
  const visit = (node) => {
    visited.add(node);
    order.push(node);
    for (const next of graph.get(node)) {
      if (!visited.has(next)) visit(next);
    }
  };
  visit(start);
  return order;
}
console.log(dfs(graph, "A"));`, output: `[ 'A', 'B', 'D', 'C', 'E' ]` },

      { type: "h2", text: "Grids are graphs" },
      { type: "code", language: "javascript", code: `// Number of islands: count groups of connected "1"s (up, down, left, right)
function numIslands(grid) {
  const rows = grid.length, cols = grid[0].length;
  let count = 0;
  const sink = (r, c) => {
    if (r < 0 || c < 0 || r >= rows || c >= cols || grid[r][c] !== "1") return;
    grid[r][c] = "0";                          // mark visited by sinking the land
    sink(r + 1, c); sink(r - 1, c); sink(r, c + 1); sink(r, c - 1);
  };
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] === "1") { count++; sink(r, c); }
    }
  }
  return count;
}

console.log(numIslands([
  ["1", "1", "0", "0", "0"],
  ["1", "1", "0", "0", "0"],
  ["0", "0", "1", "0", "0"],
  ["0", "0", "0", "1", "1"]
]));`, output: `3` },
      { type: "callout", variant: "note", text: "Each cell is a node with up to 4 neighbours. Directions are often written as `const dirs = [[1,0],[-1,0],[0,1],[0,-1]]`. For shortest paths in a grid (such as the minimum steps to an exit), use BFS. For “count or fill regions”, DFS or BFS both work." },

      { type: "h2", text: "Topological sort: ordering with prerequisites" },
      { type: "p", text: "In a **directed acyclic graph** (DAG), such as “you must learn JavaScript before React”, a topological order lists every node after all of its prerequisites. Kahn’s algorithm repeatedly takes the nodes that have no remaining prerequisites:" },
      { type: "code", language: "javascript", code: `function courseOrder(numCourses, prerequisites) {
  const graph = Array.from({ length: numCourses }, () => []);
  const inDegree = new Array(numCourses).fill(0);
  for (const [course, pre] of prerequisites) {
    graph[pre].push(course);
    inDegree[course]++;
  }

  const queue = [];
  for (let i = 0; i < numCourses; i++) if (inDegree[i] === 0) queue.push(i);

  const order = [];
  while (queue.length) {
    const c = queue.shift();
    order.push(c);
    for (const next of graph[c]) {
      if (--inDegree[next] === 0) queue.push(next);
    }
  }
  return order.length === numCourses ? order : [];   // [] means there's a cycle
}

// 0: HTML, 1: CSS, 2: JavaScript, 3: React
console.log(courseOrder(4, [[1, 0], [2, 0], [3, 2], [3, 1]]));
console.log(courseOrder(2, [[0, 1], [1, 0]]));`, output: `[ 0, 1, 2, 3 ]
[]` },

      { type: "h2", text: "Weighted shortest paths: Dijkstra" },
      { type: "p", text: "When edges have different (non-negative) costs, BFS no longer works. Dijkstra’s algorithm always expands the closest unvisited node next, using a priority queue:" },
      { type: "code", language: "javascript", code: `// graph: Map(node → [[neighbour, weight], ...])
function dijkstra(graph, source) {
  const dist = new Map([[source, 0]]);
  const pq = [[0, source]];                       // [distance, node]; use a real heap in production
  while (pq.length) {
    pq.sort((a, b) => a[0] - b[0]);
    const [d, node] = pq.shift();
    if (d > (dist.get(node) ?? Infinity)) continue; // stale entry
    for (const [next, w] of graph.get(node) || []) {
      const nd = d + w;
      if (nd < (dist.get(next) ?? Infinity)) {
        dist.set(next, nd);
        pq.push([nd, next]);
      }
    }
  }
  return dist;
}

const roads = new Map([
  ["Pune", [["Mumbai", 150], ["Nashik", 210]]],
  ["Mumbai", [["Nashik", 170], ["Goa", 590]]],
  ["Nashik", [["Goa", 520]]]
]);
console.log(Object.fromEntries(dijkstra(roads, "Pune")));`, output: `{ Pune: 0, Mumbai: 150, Nashik: 210, Goa: 730 }` },
      { type: "table", headers: ["Problem", "Algorithm", "Complexity"], rows: [
        ["Shortest path, unweighted", "BFS", "O(V + E)"],
        ["Shortest path, non-negative weights", "Dijkstra (heap)", "O((V + E) log V)"],
        ["Shortest path with negative weights", "Bellman–Ford", "O(V × E)"],
        ["Ordering with dependencies", "Topological sort (Kahn or DFS)", "O(V + E)"],
        ["Connected components or dynamic connectivity", "DFS, BFS or Union-Find", "≈ O(V + E)"],
        ["Minimum spanning tree", "Kruskal (Union-Find) or Prim", "O(E log E)"]
      ] },

      { type: "h2", text: "Union-Find (disjoint set union)" },
      { type: "code", language: "javascript", code: `class UnionFind {
  constructor(n) { this.parent = Array.from({ length: n }, (_, i) => i); this.rank = new Array(n).fill(0); }
  find(x) {
    if (this.parent[x] !== x) this.parent[x] = this.find(this.parent[x]);   // path compression
    return this.parent[x];
  }
  union(a, b) {
    let ra = this.find(a), rb = this.find(b);
    if (ra === rb) return false;                                          // already connected
    if (this.rank[ra] < this.rank[rb]) [ra, rb] = [rb, ra];
    this.parent[rb] = ra;
    if (this.rank[ra] === this.rank[rb]) this.rank[ra]++;
    return true;
  }
}

// Count friend groups among 5 people
const uf = new UnionFind(5);
[[0, 1], [1, 2], [3, 4]].forEach(([a, b]) => uf.union(a, b));
console.log(new Set([0, 1, 2, 3, 4].map((x) => uf.find(x))).size);`, output: `2` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "When do you use BFS vs DFS?", answer: ["BFS for the shortest path in unweighted graphs, minimum steps, and level-by-level processing. DFS for exploring all paths, connected components, cycle detection, topological sort and backtracking. Both are O(V + E). BFS uses a queue and memory proportional to the graph’s width, and DFS uses a stack (or recursion) proportional to its depth."] },
      { type: "qa", question: "How do you detect a cycle in a directed graph?", answer: ["DFS with three states: unvisited, visiting (on the current path) and done. Reaching a “visiting” node means a back edge, which is a cycle. Alternatively, run Kahn’s topological sort: if not every node gets processed, there is a cycle."] },
      { type: "qa", question: "Why doesn’t Dijkstra work with negative edge weights?", answer: ["It assumes that once a node is taken from the priority queue with the smallest distance, that distance is final. A negative edge found later could make it shorter, which breaks that assumption. Use Bellman–Ford for negative weights (it also detects negative cycles)."] },
      { type: "qa", question: "What is the time complexity of BFS and DFS?", answer: ["O(V + E) with an adjacency list: every vertex is visited once and every edge is examined once (twice for undirected graphs). Space is O(V) for the visited set plus the queue or stack."] },
      { type: "qa", question: "What is Union-Find used for?", answer: ["Tracking connected components as edges are added: “are a and b connected?”, counting groups, detecting cycles in undirected graphs and Kruskal’s minimum spanning tree. With path compression and union by rank, operations are nearly O(1) (inverse Ackermann)."] },

      { type: "exercise", title: "Rotting oranges", task: "In a grid, 0 is empty, 1 is a fresh orange and 2 is a rotten orange. Every minute, rotten oranges rot their 4-directional fresh neighbours. Return the minutes until no fresh orange remains, or -1 if that is impossible.", language: "javascript", solution: `function orangesRotting(grid) {
  const rows = grid.length, cols = grid[0].length;
  let queue = [], fresh = 0;

  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] === 2) queue.push([r, c]);   // multi-source BFS
      else if (grid[r][c] === 1) fresh++;
    }

  const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  let minutes = 0;
  while (queue.length && fresh > 0) {
    const next = [];
    for (const [r, c] of queue) {
      for (const [dr, dc] of dirs) {
        const nr = r + dr, nc = c + dc;
        if (nr >= 0 && nc >= 0 && nr < rows && nc < cols && grid[nr][nc] === 1) {
          grid[nr][nc] = 2;
          fresh--;
          next.push([nr, nc]);
        }
      }
    }
    queue = next;
    minutes++;                                     // one BFS level = one minute
  }
  return fresh === 0 ? minutes : -1;
}

console.log(orangesRotting([[2, 1, 1], [1, 1, 0], [0, 1, 1]]));
console.log(orangesRotting([[2, 1, 1], [0, 1, 1], [1, 0, 1]]));`, output: `4
-1`, explanation: "All rotten oranges start in the queue together (multi-source BFS), so each BFS level is exactly one minute of spreading. Counting the fresh oranges up front tells you at the end whether some were unreachable. It runs in O(rows × cols)." },

      { type: "summary", items: [
        "Graphs are nodes and edges. Adjacency lists are the default representation.",
        "BFS (a queue) gives the shortest unweighted paths, and DFS (recursion or a stack) explores components and paths.",
        "Grids are graphs with 4 or 8 neighbours. Mark cells as visited to avoid revisits.",
        "Topological sort orders dependencies and detects cycles. Dijkstra handles non-negative weights.",
        "Union-Find answers connectivity questions in nearly O(1)."
      ] }
    ]
  },

  {
    slug: "dynamic-programming",
    title: "Dynamic Programming",
    summary: "Turn exponential recursion into efficient solutions: memoisation, tabulation and the classic DP patterns.",
    readTime: 16,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Dynamic programming (DP) is the topic candidates fear most, yet it follows a repeatable recipe. It solves optimisation and counting problems (minimum cost, maximum profit, number of ways) that would otherwise take exponential time, and it appears regularly in interviews at top companies." },

      { type: "h2", text: "The mental model: remember instead of redo" },
      { type: "p", text: "Ask someone what 1+1+1+1+1 is. They count: 5. Add “+1” and ask again. They answer 6 instantly, because they **remembered** the earlier result instead of recounting. DP applies when a problem breaks into **overlapping subproblems** (the same smaller question is asked many times) with **optimal substructure** (the best answer is built from the best answers to smaller questions)." },

      { type: "h2", text: "From recursion to DP: climbing stairs" },
      { type: "p", text: "You can climb 1 or 2 steps at a time. How many distinct ways are there to reach step n?" },
      { type: "code", language: "javascript", code: `// 1. Plain recursion: ways(n) = ways(n-1) + ways(n-2), which is O(2ⁿ)
const waysSlow = (n) => (n <= 1 ? 1 : waysSlow(n - 1) + waysSlow(n - 2));

// 2. Top-down (memoisation): cache each answer, O(n)
function waysMemo(n, memo = new Map()) {
  if (n <= 1) return 1;
  if (memo.has(n)) return memo.get(n);
  const v = waysMemo(n - 1, memo) + waysMemo(n - 2, memo);
  memo.set(n, v);
  return v;
}

// 3. Bottom-up (tabulation): fill a table from the smallest case, O(n)
function waysTable(n) {
  const dp = new Array(n + 1).fill(0);
  dp[0] = 1; dp[1] = 1;
  for (let i = 2; i <= n; i++) dp[i] = dp[i - 1] + dp[i - 2];
  return dp[n];
}

// 4. Space-optimised: only the last two values are needed, O(1) space
function ways(n) {
  let prev = 1, curr = 1;
  for (let i = 2; i <= n; i++) [prev, curr] = [curr, prev + curr];
  return curr;
}

console.log(waysSlow(10), waysMemo(10), waysTable(10), ways(10), ways(45));`, output: `89 89 89 89 1836311903` },
      { type: "table", headers: ["", "Top-down (memoisation)", "Bottom-up (tabulation)"], rows: [
        ["Approach", "Recursion plus a cache", "Iterative table filling"],
        ["Computes", "Only the subproblems that are needed", "Every subproblem"],
        ["Risk", "Stack overflow for deep recursion", "Need to work out the fill order"],
        ["Space optimisation", "Harder", "Easy (keep the last row or values)"],
        ["Easiest to write first", "Usually this one", ""]
      ] },

      { type: "h2", text: "The 5-step DP recipe" },
      { type: "ol", items: [
        "**Define the state:** what does `dp[i]` (or `dp[i][j]`) mean, in words? For example, “the minimum coins to make amount i”.",
        "**Find the recurrence:** how does `dp[i]` depend on smaller states? Think about the **last decision** made.",
        "**Set the base cases:** the smallest inputs with known answers.",
        "**Choose the order:** fill the table so every dependency is computed first.",
        "**Return the answer:** often `dp[n]`, sometimes the max or min over the whole table."
      ] },

      { type: "h2", text: "Pattern 1: 1D decisions (House Robber)" },
      { type: "code", language: "javascript", code: `// Rob houses for maximum money, but never two adjacent houses.
// State: dp[i] = best total using houses 0..i
// Last decision: rob house i (dp[i-2] + nums[i]) or skip it (dp[i-1])
function rob(nums) {
  let prev2 = 0, prev1 = 0;
  for (const x of nums) {
    const curr = Math.max(prev1, prev2 + x);
    prev2 = prev1;
    prev1 = curr;
  }
  return prev1;
}
console.log(rob([2, 7, 9, 3, 1]));`, output: `12` },

      { type: "h2", text: "Pattern 2: unbounded choices (Coin Change)" },
      { type: "code", language: "javascript", code: `// Fewest coins to make the amount (unlimited coins of each type)
// State: dp[a] = min coins for amount a
// Recurrence: dp[a] = 1 + min(dp[a - coin]) over every coin <= a
function coinChange(coins, amount) {
  const dp = new Array(amount + 1).fill(Infinity);
  dp[0] = 0;
  for (let a = 1; a <= amount; a++) {
    for (const coin of coins) {
      if (coin <= a) dp[a] = Math.min(dp[a], dp[a - coin] + 1);
    }
  }
  return dp[amount] === Infinity ? -1 : dp[amount];
}
console.log(coinChange([1, 5, 10, 25], 63), coinChange([2], 3));`, output: `6 -1` },
      { type: "callout", variant: "warning", text: "Greedy (always take the biggest coin) fails for coin sets like [1, 3, 4] with amount 6: greedy picks 4+1+1 (3 coins), but 3+3 (2 coins) is optimal. When greedy is not provably correct, DP is the safe choice." },

      { type: "h2", text: "Pattern 3: 0/1 knapsack" },
      { type: "code", language: "javascript", code: `// Each item can be taken at most once. Maximise value within the weight capacity.
// dp[w] = best value with capacity w; iterate w DOWNWARD so each item is used once
function knapsack(weights, values, capacity) {
  const dp = new Array(capacity + 1).fill(0);
  for (let i = 0; i < weights.length; i++) {
    for (let w = capacity; w >= weights[i]; w--) {
      dp[w] = Math.max(dp[w], dp[w - weights[i]] + values[i]);
    }
  }
  return dp[capacity];
}
console.log(knapsack([1, 3, 4, 5], [1, 4, 5, 7], 7));`, output: `9` },
      { type: "p", text: "Iterating capacity **downwards** ensures `dp[w - weight]` still holds the value from **before** this item was considered, so it is used at most once. Iterating upwards would turn it into unbounded knapsack, like coin change." },

      { type: "h2", text: "Pattern 4: two sequences (Longest Common Subsequence)" },
      { type: "code", language: "javascript", code: `// dp[i][j] = LCS length of a[0..i) and b[0..j)
function lcs(a, b) {
  const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1] + 1                      // characters match: extend
        : Math.max(dp[i - 1][j], dp[i][j - 1]);     // skip one character
    }
  }
  return dp[a.length][b.length];
}
console.log(lcs("abcde", "ace"), lcs("codeazy", "academy"));`, output: `3 4` },
      { type: "p", text: "The same 2D table shape solves **edit distance** (spell-check, diff tools), longest common substring and many string-alignment problems." },

      { type: "h2", text: "Pattern 5: grid paths" },
      { type: "code", language: "javascript", code: `// Minimum path sum from the top-left to the bottom-right, moving only right or down
function minPathSum(grid) {
  const m = grid.length, n = grid[0].length;
  const dp = Array.from({ length: m }, () => new Array(n).fill(0));
  for (let r = 0; r < m; r++) {
    for (let c = 0; c < n; c++) {
      if (r === 0 && c === 0) dp[r][c] = grid[0][0];
      else if (r === 0) dp[r][c] = dp[r][c - 1] + grid[r][c];
      else if (c === 0) dp[r][c] = dp[r - 1][c] + grid[r][c];
      else dp[r][c] = Math.min(dp[r - 1][c], dp[r][c - 1]) + grid[r][c];
    }
  }
  return dp[m - 1][n - 1];
}
console.log(minPathSum([[1, 3, 1], [1, 5, 1], [4, 2, 1]]));`, output: `7` },

      { type: "h2", text: "Pattern 6: Longest Increasing Subsequence" },
      { type: "code", language: "javascript", code: `// O(n²) DP: dp[i] = length of the LIS ending at index i
function lengthOfLIS(nums) {
  const dp = new Array(nums.length).fill(1);
  for (let i = 1; i < nums.length; i++)
    for (let j = 0; j < i; j++)
      if (nums[j] < nums[i]) dp[i] = Math.max(dp[i], dp[j] + 1);
  return Math.max(...dp);
}
console.log(lengthOfLIS([10, 9, 2, 5, 3, 7, 101, 18]));`, output: `4` },
      { type: "callout", variant: "note", text: "LIS has a famous O(n log n) solution using binary search over “smallest tail for each length”. It is worth learning once you are comfortable with the O(n²) version." },

      { type: "h2", text: "Recognising a DP problem" },
      { type: "table", headers: ["Clue", "Example"], rows: [
        ["“How many ways…”", "Climbing stairs, decode ways, unique paths"],
        ["“Minimum or maximum cost, profit or length”", "Coin change, house robber, LIS"],
        ["“Can you reach or form…” (true or false)", "Word break, partition equal subset sum"],
        ["Choices at each step affect future options", "Knapsack, stock buy and sell with cooldown"],
        ["Two strings or sequences compared", "LCS, edit distance"]
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What are the two properties a problem needs for DP?", answer: ["**Overlapping subproblems**: the same subproblems are solved repeatedly in a naive recursion. **Optimal substructure**: an optimal solution can be built from optimal solutions to subproblems. If the subproblems don’t overlap, plain divide and conquer is enough."] },
      { type: "qa", question: "What is the difference between memoisation and tabulation?", answer: ["Memoisation is top-down: write the natural recursion and cache the results, computing only what is needed. Tabulation is bottom-up: iteratively fill a table from the base cases in dependency order. It avoids recursion limits and makes space optimisation easier."] },
      { type: "qa", question: "How do you approach a new DP problem?", answer: ["Start with a brute-force recursion that makes one decision at a time. Identify the parameters that change, which become the state. Write the recurrence by considering the last decision, add memoisation, then convert to bottom-up and optimise space if needed. Always define the state in words first."] },
      { type: "qa", question: "Why iterate the capacity backwards in 0/1 knapsack with a 1D array?", answer: ["So that `dp[w - weight]` still holds the value from before the current item, which guarantees each item is used at most once. Iterating forwards would let the item be reused within the same pass, which solves the unbounded knapsack instead."] },
      { type: "qa", question: "What is the complexity of LCS?", answer: ["O(m × n) time for strings of lengths m and n, and O(m × n) space for the full table, which can be reduced to O(min(m, n)) by keeping only two rows if you don’t need to reconstruct the subsequence."] },

      { type: "exercise", title: "Word break", task: "Given a string `s` and a dictionary of words, return true if `s` can be split into a sequence of dictionary words. Example: \"codeazylearn\" with [\"code\", \"azy\", \"learn\", \"codeazy\"] → true.", language: "javascript", solution: `function wordBreak(s, wordDict) {
  const words = new Set(wordDict);
  // dp[i] = can s[0..i) be segmented?
  const dp = new Array(s.length + 1).fill(false);
  dp[0] = true;                                   // empty prefix
  for (let i = 1; i <= s.length; i++) {
    for (let j = 0; j < i; j++) {
      if (dp[j] && words.has(s.slice(j, i))) {    // last word is s[j..i)
        dp[i] = true;
        break;
      }
    }
  }
  return dp[s.length];
}

console.log(wordBreak("codeazylearn", ["code", "azy", "learn", "codeazy"]));
console.log(wordBreak("catsandog", ["cats", "dog", "sand", "and", "cat"]));`, output: `true
false`, explanation: "The state is “can the prefix of length i be built?”. The last decision is which dictionary word ends at position i. If the prefix before it (`dp[j]`) is buildable and `s[j..i)` is a word, then `dp[i]` is true. This is O(n²) checks, each with an O(n) slice, so roughly O(n³) in the worst case. Capping j by the longest word length helps a lot in practice." },

      { type: "summary", items: [
        "DP means remembering subproblem answers. It needs overlapping subproblems and optimal substructure.",
        "Write the recursion, memoise it, then convert to a bottom-up table and optimise space.",
        "The recipe: state, recurrence (from the last decision), base cases, order, answer.",
        "Core patterns: 1D decisions, unbounded and 0/1 knapsack, two sequences (LCS), grid paths and LIS.",
        "Clues: “number of ways”, “min or max” and “can we form”, with choices affecting the future."
      ] }
    ]
  }
];
